/**
 * The two provider-loss actions on the Bexio drafts page, kept out of the page file.
 *
 * BexioDraftsPage is already ~1700 lines, so CLAUDE.md Regel 48 asks for an extract
 * rather than more inline handlers. This hook owns only the request/-in-flight state
 * for the pair; the page keeps deciding what to render.
 *
 * NEITHER action retries. The check is one provider GET server-side; the replacement
 * can burn a real Bexio document number. Both leave the reload to the caller so the
 * ledger — not this hook's optimism — decides what the row becomes.
 */
import { useCallback, useState } from "react";
import { toast } from "sonner";

import {
  BexioDraftCreateError,
  confirmBexioDraftProviderMissing,
  replaceBexioDraftProviderMissing,
} from "../../../api/adminData/bexio";

export default function useBexioDraftProviderMissing({ onChanged }) {
  const [checkingId, setCheckingId] = useState(null);
  const [replaceRow, setReplaceRow] = useState(null);
  const [replacing, setReplacing] = useState(false);

  /**
   * Ask the backend to verify one created claim's Bexio document.
   *
   * A 409 is the NORMAL negative answer here, not a crash: it means either the
   * document is still there or the check was inconclusive. In both cases nothing
   * changed and the row must stay exactly as it was, so this reports the server's
   * German detail text and reloads rather than guessing.
   */
  const checkProvider = useCallback(
    async (row) => {
      const requestId = row?.ledger?.id;
      if (!requestId) {
        toast.error("Für diese Zeile ist kein Ledger-Eintrag geladen.");
        return;
      }
      setCheckingId(row.tenancyId);
      try {
        await confirmBexioDraftProviderMissing(requestId);
        toast.warning(
          "Der Bexio-Entwurf existiert nicht mehr. Ein Ersatzentwurf kann jetzt erstellt werden."
        );
      } catch (e) {
        if (e instanceof BexioDraftCreateError) {
          // The server distinguishes "still exists" from "inconclusive" in its
          // detail text; the client never re-derives that distinction.
          toast.message(e.message);
        } else {
          toast.error(e?.message || "Prüfung konnte nicht durchgeführt werden.");
        }
      } finally {
        setCheckingId(null);
        await onChanged?.();
      }
    },
    [onChanged]
  );

  /**
   * Create the replacement. Only reachable from a row the server already moved to
   * provider_missing, and only after the modal's explicit confirmation.
   */
  const submitReplace = useCallback(async () => {
    const row = replaceRow;
    const requestId = row?.ledger?.id;
    if (!requestId) {
      setReplaceRow(null);
      return;
    }
    setReplacing(true);
    try {
      const res = await replaceBexioDraftProviderMissing(requestId);
      const nr = res?.document_nr;
      toast.success(
        nr
          ? `Ersatzentwurf ${nr} wurde erstellt.`
          : "Ersatzentwurf wurde erstellt."
      );
    } catch (e) {
      if (e instanceof BexioDraftCreateError) {
        toast.error(`Ersatzentwurf nicht erstellt: ${e.message}`);
      } else {
        toast.error(e?.message || "Ersatzentwurf konnte nicht erstellt werden.");
      }
    } finally {
      setReplacing(false);
      setReplaceRow(null);
      await onChanged?.();
    }
  }, [replaceRow, onChanged]);

  return {
    checkingId,
    checkProvider,
    replaceRow,
    openReplace: setReplaceRow,
    cancelReplace: () => setReplaceRow(null),
    replacing,
    submitReplace,
  };
}
